import type { LocaleCode } from '@fixit/bozp-content';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'bozp.locale';
const SUPPORTED: LocaleCode[] = ['cs-CZ', 'en-US'];

/**
 * Player locale: seeded from the `?locale=` URL param (deep-linkable — the
 * mobile app passes the user's app language when opening the training link),
 * then from the sessionStorage of a previous in-tab choice, defaulting to
 * Czech. The in-player toggle persists per tab so route navigations that drop
 * the search param keep the chosen language.
 */
function initialLocale(): LocaleCode {
  const fromUrl = new URLSearchParams(window.location.search).get('locale');
  if (fromUrl && SUPPORTED.includes(fromUrl as LocaleCode)) return fromUrl as LocaleCode;
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored as LocaleCode)) return stored as LocaleCode;
  } catch {
    // sessionStorage unavailable (private mode restrictions) — fall through to default
  }
  return 'cs-CZ';
}

const LocaleContext = createContext<{ locale: LocaleCode; setLocale: (l: LocaleCode) => void }>({
  locale: 'cs-CZ',
  setLocale: () => undefined,
});

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(initialLocale);
  const setLocale = useCallback((next: LocaleCode) => {
    setLocaleState(next);
    try {
      sessionStorage.setItem(STORAGE_KEY, next);
    } catch {
      // best-effort persistence only
    }
  }, []);
  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleCode {
  return useContext(LocaleContext).locale;
}

/** Compact cs/en switch pill, rendered in the player shell on every route. */
export function LocaleToggle() {
  const { locale, setLocale } = useContext(LocaleContext);
  return (
    <div
      data-testid="locale-toggle"
      className="fixed top-3 right-3 z-50 flex overflow-hidden rounded-full border border-stone-300 bg-white text-xs font-semibold shadow-sm"
    >
      {SUPPORTED.map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`locale-${l}`}
          aria-pressed={locale === l}
          onClick={() => setLocale(l)}
          className={
            locale === l
              ? 'bg-stone-900 px-2.5 py-1 text-white'
              : 'px-2.5 py-1 text-stone-500 hover:text-stone-900'
          }
        >
          {l === 'cs-CZ' ? 'CS' : 'EN'}
        </button>
      ))}
    </div>
  );
}
